import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@components/ui/sonner";
import { AppErrorBoundary } from "@components/shared/AppErrorBoundary";
import { useJwtRefresh } from "@hooks/useJwtRefresh";
import {
  usePageHydration,
  usePosthogIdentify,
  useSentryIdentify,
  useFlashMessages,
} from "@hooks/layout";
import { ToolLabelsProvider } from "@launch10/use-langgraph-stream";
import { getToolLabelMap } from "@launch10/shared/mcp/registry";
import { UploadManagerMount } from "@components/uploads/UploadManagerMount";

// One QueryClient for the whole app — created at module scope so navigations
// between SiteLayout and SiteLight don't blow away the cache.
const queryClient = new QueryClient();

// Build the chat-indicator label map ONCE at module load. The registry
// is a static JSON artifact (regenerated by `rake mcp:export`), so a
// per-render call would be wasted work. Operation/resource labels are
// declared on Rails controllers via `mcp_resource label:` and
// `mcp_callable label:`; the exporter writes them into the registry;
// this read pulls them through. The use-langgraph-stream package
// stays domain-agnostic — it has no idea what "uploads" means; we
// teach it here.
const TOOL_LABELS = {
  ...getToolLabelMap(),
  // Bespoke (non-MCP) tools still in use by the brainstorm / website
  // agents. These names don't have a Rails-side declaration site
  // (they're TypeScript tools), so the labels live here next to the
  // provider. Move into the chat package's defaults if/when one feels
  // truly universal across all consumers.
  tools: {
    save_answers: "Saving your answers",
    save_social_links: "Saving social links",
    searchIcons: "Looking up icons",
    read_file: "Reading your site files",
    write_file: "Writing site files",
    edit_file: "Editing your site",
    delete_file: "Removing a file",
    ls: "Listing site files",
    glob: "Searching site files",
    grep: "Searching site files",
    load_skill: "Loading guidance",
    faq: "Checking FAQs",
    ask_user_question: "Writing question",
    execute: "Running a command",
    commit_template: "Setting up your template",
    preview_screenshot: "Checking the preview",
    update_campaign_assets: "Updating your ads",
    search_domains: "Searching domains",
  },
  // Aggregation buckets for the ToolActivity card's tallies
  // ("Read 3 · Edited 2 · Searched 4"). Deepagents/MCP universals
  // (read_file, edit_file, glob, grep, search, …) are covered by the
  // package's DEFAULT_TOOL_CATEGORIES; only bespoke tools need a row
  // here. Anything unmapped lands in the generic "work" bucket —
  // visible by default.
  categories: {
    searchIcons: "search",
    search_domains: "search",
    faq: "search",
  },
  // Friendly names for `task` delegation rows ("Site builder: Update
  // the navigation…"). Keys are deepagents subagent_type values.
  agents: {
    coder: "Site builder",
    "template-selector": "Template picker",
    "general-purpose": "Helper",
  },
  // Tools whose output renders its own UI, so a "ran a tool" chip is just noise.
  // create_or_edit_artifact surfaces the reviewable artifact card itself;
  // ask_user_question surfaces the choice card (and reifies into Q/A messages);
  // write_todos surfaces as the TodoList.
  hidden: ["create_or_edit_artifact", "ask_user_question", "write_todos"],
} as const;

/**
 * Universal page providers: identity (PostHog, Sentry), JWT refresh, flash
 * bridging, page hydration, error boundary, query client, toaster.
 *
 * Every top-level layout (SiteLayout, SiteLight) composes this. Individual
 * pages should not — they should pick a layout.
 */
export function PageProviders({ children }: { children: React.ReactNode }) {
  usePageHydration();
  usePosthogIdentify();
  useSentryIdentify();
  useJwtRefresh();
  useFlashMessages();

  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToolLabelsProvider value={TOOL_LABELS}>
          {children}
          <Toaster position="top-right" />
          <UploadManagerMount />
        </ToolLabelsProvider>
      </QueryClientProvider>
    </AppErrorBoundary>
  );
}
